import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
  type Router,
  type RouterScrollBehavior,
} from 'vue-router'
import { handleVerifyToken, handleCodeExchange, handleResetToken } from '@/composables/useAuthTokens'
import {
  BRAND_DETAILS_ROUTE,
  BRANDS_ROUTE,
  CAPSULE_ROUTE,
  COMPARISON_ROUTE,
  FAVORITES_ROUTE,
  HOME_ALIASES,
  HOME_ROUTE,
  LISTS_ROUTE,
  PRIVACY_POLICY_ROUTE,
  PRODUCT_COMPARE_ROUTE,
  PRODUCT_ROUTE,
  PRODUCT_SIMILAR_ROUTE,
  PROFILE_ROUTE,
  PROFILE_PERSONAL_DATA_ROUTE,
  PROFILE_NOTIFICATIONS_ROUTE,
  PROFILE_NOTIFICATIONS_LIST_ROUTE,
  PROFILE_MY_PARAMETERS_ROUTE,
  PROFILE_SIZES_ROUTE,
  PROFILE_MY_STYLE_ROUTE,
  RECOMMENDATION_BANNERS_ROUTE,
  RECOMMENDATION_COLLECTIONS_ROUTE,
  RESET_PASSWORD_ROUTE,
  SIM_ROUTE,
  SEARCH_ROUTE,
  STYLE_SPACE_ROUTE,
  TERMS_OF_SERVICE_ROUTE,
  TEST_ROUTE,
  PAGE_LOADER_PREVIEW_ROUTE,
  VERIFY_ROUTE
} from '@/router/routes'
import { useTokenStore, useAuthModal } from '@/features/auth/store'
import {
  brandLetterSectionId,
  normalizeFairBrandLetter,
} from '@/widgets/brands/fair/fair-brands-page.data'
import { applyFairSeo } from '@/composables/useFairSeo'
import { buildFairSearchSeo, getFairRouteSeo } from '@/seo/routeSeo'
import { shouldStartFairPageLoader } from '@/features/page-loader/fairPageLoader.guards'
import { useFairPageLoaderStore } from '@/features/page-loader/store'

export const routes: RouteRecordRaw[] = [
  {
    path: HOME_ROUTE,
    name: 'home',
    component: () => import('@/pages/HomePage.vue'),
    alias: HOME_ALIASES,
    meta: { pageLoader: true },
  },
  {
    path: VERIFY_ROUTE,
    name: 'verify-user',
    component: () => import('@/pages/HomePage.vue'),
    beforeEnter: async (to) => {
      await handleVerifyToken(to.params.verifyToken as string)
      return { name: 'home' }
    }
  },
  {
    path: RESET_PASSWORD_ROUTE,
    name: 'reset-password',
    component: () => import('@/pages/HomePage.vue'),
    beforeEnter: (to) => {
      handleResetToken(to.params.resetToken as string)
      return { name: 'home' }
    }
  },
  {
    path: TEST_ROUTE,
    name: 'chatbot',
    component: () => import('@/aiAsisstant/ChatBotPage.vue')
  },
  {
    path: PAGE_LOADER_PREVIEW_ROUTE,
    name: 'page-loader-preview',
    component: () => import('@/pages/FairPageLoaderPreviewPage.vue'),
  },
  {
    path: BRANDS_ROUTE,
    name: 'brands',
    component: () => import('@/pages/FairBrandsPage.vue')
  },
  {
    path: BRAND_DETAILS_ROUTE,
    name: 'brand-details',
    component: () => import('@/pages/FairBrandDetailPage.vue'),
    props: true,
    meta: { pageLoader: true },
  },
  {
    path: PROFILE_ROUTE,
    name: 'profile',
    component: () => import('@/pages/ProfilePage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: PROFILE_PERSONAL_DATA_ROUTE,
    name: 'profile-personal-data',
    component: () => import('@/pages/profile/PersonalDataPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: PROFILE_NOTIFICATIONS_ROUTE,
    name: 'profile-notifications',
    component: () => import('@/pages/profile/NotificationSettingsPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: PROFILE_NOTIFICATIONS_LIST_ROUTE,
    name: 'profile-notifications-list',
    component: () => import('@/pages/profile/NotificationsPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: PROFILE_MY_PARAMETERS_ROUTE,
    name: 'profile-my-parameters',
    component: () => import('@/pages/profile/MyParametersPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: PROFILE_SIZES_ROUTE,
    name: 'profile-sizes',
    component: () => import('@/pages/profile/SizesPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: PROFILE_MY_STYLE_ROUTE,
    name: 'profile-my-style',
    component: () => import('@/pages/profile/MyStylePage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: FAVORITES_ROUTE,
    name: 'favorites',
    component: () => import('@/pages/FavoritesPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: COMPARISON_ROUTE,
    name: 'comparison',
    component: () => import('@/pages/FairVariantComparePage.vue'),
    meta: { pageLoader: true },
  },
  {
    path: LISTS_ROUTE,
    name: 'lists',
    component: () => import('@/pages/FavoritesPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: CAPSULE_ROUTE,
    name: 'capsule',
    component: () => import('@/pages/CapsulePageTest.vue')
  },
  {
    path: RECOMMENDATION_BANNERS_ROUTE,
    name: 'recommendation-banners',
    component: () => import('@/pages/FairSelectionPage.vue'),
    meta: { pageLoader: true },
  },
  {
    path: RECOMMENDATION_COLLECTIONS_ROUTE,
    name: 'recommendation-collections',
    component: () => import('@/features/recommendations/ui/CollectionsList.vue')
  },
  {
    path: SEARCH_ROUTE,
    name: 'search',
    component: () => import('@/pages/SearchPage.vue'),
    props: route => ({ searchText: route.params.searchText ?? '' }),
    meta: { pageLoader: true },
  },
  {
    path: STYLE_SPACE_ROUTE,
    name: 'style-space',
    component: () => import('@/pages/FairStyleSpacePage.vue'),
    meta: { pageLoader: true },
    // Every /playground/* route wants a JWT: a guest gets the auth modal and
    // lands on Home, the way the persona sections treat a guest there.
    beforeEnter: () => {
      if (useTokenStore().isAuthenticated()) return true
      if (typeof window !== 'undefined') {
        useAuthModal().showAuthModal('signInSignUpWrapper')
      }
      return { name: 'home' }
    },
  },
  {
    path: SIM_ROUTE,
    name: 'sim',
    component: () => import('@/pages/SearchSimilarPage.vue'),
    meta: { pageLoader: true },
  },
  {
    path: PRODUCT_SIMILAR_ROUTE,
    name: 'product-similar',
    component: () => import('@/pages/FairProductSimilarPage.vue'),
    meta: { pageLoader: true },
  },
  {
    path: PRODUCT_ROUTE,
    name: 'product',
    component: () => import('@/pages/ProductPage.vue'),
    meta: { pageLoader: true },
  },
  {
    path: PRODUCT_COMPARE_ROUTE,
    name: 'product-compare',
    component: () => import('@/pages/ProductComparePage.vue'),
    meta: { pageLoader: true },
  },
  {
    path: PRIVACY_POLICY_ROUTE,
    name: 'privacy-policy',
    component: () => import('@/pages/PrivacyPolicyPage.vue')
  },
  {
    path: TERMS_OF_SERVICE_ROUTE,
    name: 'terms-of-service',
    component: () => import('@/pages/TermsOfServicePage.vue')
  }
]

function resolveBrandsLetterFromQuery(
  letter: unknown,
): string {
  if (Array.isArray(letter)) {
    return normalizeFairBrandLetter(letter[0])
  }
  return normalizeFairBrandLetter(typeof letter === 'string' ? letter : '')
}

async function scrollToBrandsLetterElement(
  letter: string,
  behavior: ScrollBehavior = 'smooth',
): Promise<boolean> {
  if (typeof document === 'undefined') return false
  const normalized = normalizeFairBrandLetter(letter)
  if (!normalized) return false
  const id = brandLetterSectionId(normalized)

  for (let attempt = 0; attempt < 24; attempt += 1) {
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior, block: 'start' })
      return true
    }
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => resolve())
    })
  }
  return false
}

export const scrollBehavior: RouterScrollBehavior = (to, _from, savedPosition) => {
  if (savedPosition) {
    return savedPosition
  }

  if (typeof document === 'undefined') {
    return { top: 0 }
  }

  if (to.hash) {
    return new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        const element = document.querySelector(to.hash)
        if (element) {
          element.scrollIntoView({ behavior: 'auto', block: 'start' })
        }
        resolve()
      })
    }).then(() => {
      if (document.querySelector(to.hash)) {
        return { el: to.hash, behavior: 'auto' as ScrollBehavior }
      }
      return { top: 0 }
    })
  }

  if (to.name === 'brands') {
    const letter = resolveBrandsLetterFromQuery(to.query.letter)
    if (letter) {
      return scrollToBrandsLetterElement(letter, 'smooth').then(() => false)
    }
  }

  return { top: 0, behavior: 'auto' as ScrollBehavior }
}

/** Auth + SEO guards — attach to ViteSSG router or standalone factory. */
export function registerRouterGuards(router: Router): void {
  router.beforeEach(async (to, from) => {
    const tokenStore = useTokenStore()

    if (to.query.code && !from.query.code) {
      await handleCodeExchange(to.query.code as string)
      const query = { ...to.query }
      delete query.code
      return { path: to.path, query, hash: to.hash }
    }

    if (to.meta.requiresAuth && !tokenStore.isAuthenticated()) {
      if (typeof window !== 'undefined') {
        useAuthModal().showAuthModal('signIn')
      }
      return false
    }

    if (typeof window !== 'undefined') {
      const pageLoader = useFairPageLoaderStore()
      if (shouldStartFairPageLoader(to, from)) {
        pageLoader.start()
      } else if (pageLoader.isActive && !to.meta.pageLoader) {
        pageLoader.fail()
      }
    }

    return true
  })

  router.afterEach((to) => {
    if (to.name === 'search') {
      applyFairSeo(buildFairSearchSeo(to))
      return
    }
    applyFairSeo(getFairRouteSeo(to.name, to.path))
  })

  router.onError(() => {
    if (typeof window !== 'undefined') {
      useFairPageLoaderStore().fail()
    }
  })
}

let activeRouter: Router | null = null

/** Bind the live app router (ViteSSG instance) for legacy `import router from '@/router'`. */
export function setActiveRouter(router: Router): void {
  activeRouter = router
}

export function createAppRouter(): Router {
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    scrollBehavior,
  })
  registerRouterGuards(router)
  setActiveRouter(router)
  return router
}

function getActiveRouter(): Router {
  if (!activeRouter) {
    return createAppRouter()
  }
  return activeRouter
}

const router = new Proxy({} as Router, {
  get(_target, prop, receiver) {
    const instance = getActiveRouter()
    const value = Reflect.get(instance, prop, receiver)
    return typeof value === 'function' ? value.bind(instance) : value
  },
})

export default router
